import { computed, type MaybeRefOrGetter, toValue } from 'vue'
import { useI18n } from 'vue-i18n'
import { DialogPhase, type Dialog } from '@/core/models/dialog/dialog'
import { useDisplayDate } from '@/core/composables/use-display-date'

export type TimelineStepState = 'done' | 'current' | 'upcoming'

export interface TimelineStep {
  id: string
  title: string
  subInformation?: string[]
  state: TimelineStepState
}

const phaseToTimelineIndex: Record<DialogPhase, number> = {
  [DialogPhase.Feedback]: 1,
  [DialogPhase.CoachPreparation]: 2,
  [DialogPhase.Conversation]: 3,
  [DialogPhase.Conducted]: 4,
}

export const useCoacheeDialogTimeline = (
  dialog: MaybeRefOrGetter<Omit<Dialog, 'coachee' | 'coach'>>,
) => {
  const { t } = useI18n()

  const activeTimelineIndex = computed(() => {
    return phaseToTimelineIndex[toValue(dialog).phase] ?? 0
  })

  const lineClass = (isActive: boolean) => (isActive ? 'bg-primary' : 'bg-outline-variant')

  const timelineSteps = computed<TimelineStep[]>(() => {
    const currentDialog = toValue(dialog)
    const baseSteps: Omit<TimelineStep, 'state'>[] = [
      {
        id: 'created',
        title: t('dialog.timeline.created'),
        subInformation: [displayDate(currentDialog.createdAt)],
      },
      {
        id: 'coachee-preparation',
        title: t('dialog.timeline.coacheePreparation'),
        subInformation: coacheeReleaseInformation.value,
      },
      {
        id: 'coach-preparation',
        title: t('dialog.timeline.coachPreparation'),
        subInformation: coachReleaseInformation.value,
      },
      {
        id: 'dialog',
        title: t('dialog.timeline.dialog'),
        subInformation: currentDialog.plannedAt
          ? [displayDate(currentDialog.plannedAt)]
          : undefined,
      },
      {
        id: 'dialog-conducted',
        title: t('dialog.timeline.done'),
        subInformation: currentDialog.conductedAt
          ? [displayDate(currentDialog.conductedAt)]
          : undefined,
      },
    ]

    return baseSteps.map((step, index) => {
      let state: TimelineStepState = 'upcoming'

      if (index < activeTimelineIndex.value) {
        state = 'done'
      } else if (index === activeTimelineIndex.value) {
        state = 'current'
      }

      return { ...step, state }
    })
  })

  const coacheeReleaseInformation = computed(() => {
    const currentDialog = toValue(dialog)

    const information: string[] = []

    if (currentDialog.leadTimeStartsAt) {
      information.push(t('dialog.until') + ' ' + displayDate(currentDialog.leadTimeStartsAt))
    }

    if (currentDialog.coacheeDataReleasedAt) {
      information.push(
        t('dialog.release.label') + ': ' + displayDate(currentDialog.coacheeDataReleasedAt),
      )
    }
    return information
  })

  const coachReleaseInformation = computed(() => {
    const currentDialog = toValue(dialog)

    const information: string[] = []

    if (currentDialog.coachDataReleasedAt) {
      information.push(
        t('dialog.release.label') + ': ' + displayDate(currentDialog.coachDataReleasedAt),
      )
    }
    return information
  })

  const displayDate = (date: string) => {
    return useDisplayDate(date, { dayName: 'short', monthName: 'short' }).value
  }

  return {
    activeTimelineIndex,
    lineClass,
    timelineSteps,
  }
}
